import { adminUtils, capturePosthogEvent, log, userUtils, utils } from '@/helpers';
import { type AllTiers, type BasicTiers, Role, type User } from '@/legacy-types';
import { hasLoyaltyAppV2Enabled } from '@/pages/settings/loyalty-app';
import { type AppNavLink, Flex, OmniSearchOption, OmniSearchTargetType } from '@alpine-iq/ui-library';
import React from 'react';
import { LightningIcon } from '../ux/UpsellBanner';

type AppNavLinkWithoutIcon = Omit<AppNavLink, 'icon'>;

export type AiqNavLink = AppNavLinkWithoutIcon & {
	icon?: AppNavLink['icon'];
	children?: AiqNavLink[];
	route?: string;
	exactRoute?: boolean;
	disabled?: boolean; // Filled by isDisabled function
	render?: (props: AiqNavLink, ctx: NavbarContext) => JSX.Element | React.ReactNode | undefined;
	isDisabled?: (props: AiqNavLink) => boolean;
	isShown?: (props: AiqNavLink, ctx: NavbarContext) => boolean;
	omniSearch?: OmniSearchOption[];
	getTitle?: (params: { [key: string]: string }, query: { [key: string]: string }) => string;
};

export type NavbarContext = {
	uid: string;
	email: string;
	isAdmin?: boolean;
	isOwner?: boolean;
	isViewingAgency?: boolean;
	hasEcommerce?: boolean;
	hasRole: {
		owner: boolean;
		ma: boolean;
		marketing: boolean;
		operator: boolean;
		analyst: boolean;
		manager: boolean;
	};
	isLoyaltyAppV2Enabled: boolean;
	isBrandAnalyticsV2Enabled: boolean;
	isRetailAnalyticsV2Enabled: boolean;
};

export const getNavbarContext = (
	{ id = '', type, ...user }: User,
	{ isLoyaltyAppV2Enabled, isBrandAnalyticsV2Enabled, isRetailAnalyticsV2Enabled }: Partial<NavbarContext>,
): NavbarContext => {
	return {
		uid: id ?? '',
		email: utils.auth.getEmail(),
		isAdmin: userUtils.isAdmin(),
		isOwner: userUtils.hasRoleView(Role.OWNER),
		isViewingAgency: type === 'agency' || id === '1',
		hasEcommerce: user.metadata?.hasEcommerce ?? false,
		hasRole: {
			owner: userUtils.hasRoleView(Role.OWNER),
			ma: userUtils.hasRoleView(Role.MA),
			marketing: userUtils.hasRoleView(Role.MARKETING),
			analyst: userUtils.hasRoleView(Role.ANALYST),
			operator: userUtils.hasRoleView(Role.OPERATOR),
			manager: userUtils.hasRoleView(Role.MANAGER),
		},
		isLoyaltyAppV2Enabled: isLoyaltyAppV2Enabled ?? false,
		isBrandAnalyticsV2Enabled: isBrandAnalyticsV2Enabled ?? false,
		isRetailAnalyticsV2Enabled: isRetailAnalyticsV2Enabled ?? false,
	};
};
const inverted = (fn: (props: AiqNavLink, ctx: NavbarContext) => boolean) => (props: AiqNavLink, ctx: NavbarContext) => !fn(props, ctx);

const isViewingAgency = (props: AiqNavLink, ctx: NavbarContext) => ctx.isViewingAgency || utils.isAdminAgency();

export const adminOr = (b?: boolean): boolean => userUtils.isAdmin() || b || false;
export const hasLoyalty = (tier: BasicTiers) => userUtils.hasPackageTier('loyalty', tier);
export const hasMarketing = (tier: BasicTiers) => userUtils.hasPackageTier('marketing', tier);
export const hasRetailAnalytics = (tier: BasicTiers) => userUtils.hasPackageTier('analytics', tier);
export const hasBrandAnalytics = (tier: AllTiers) => userUtils.hasPackageTier('brandAnalytics', tier);
export const hasDataOps = (tier: BasicTiers) => userUtils.hasPackageTier('dataops', tier);
export const hasApp = (tier: BasicTiers) => userUtils.hasPackageTier('nativeApps', tier);

const isPoliticalAgency = () => utils.isDispojoy() || utils.isMessageDigital() || utils.isWAB() || utils.isPolitical();

export const ALERTS_MANAGER = 'Alerts Manager';
export const aiqAppNavLinks: AiqNavLink[] = [
	{
		label: 'Dashboard',
		icon: 'HOME',
		route: '/missionControl/:uid',
		omniSearch: [
			{
				route: '/missionControl/:uid',
				label: 'Mission control',
				key: 'mission-control',
				breadcrumbs: ['Mission control'],
				type: OmniSearchTargetType.INTERNAL_LINK,
				keywords: ['dashboard', 'mission control', 'mission', 'control'],
			},
		],
		isShown: (props: AiqNavLink, ctx: NavbarContext) => {
			if (isViewingAgency(props, ctx)) return false;
			return adminOr(ctx.hasRole.manager);
		},
	},
	{
		label: 'Customers',
		icon: 'CRM',
		isShown: inverted(isViewingAgency),
		children: [
			{
				label: 'Personas',
				route: '/personas/:uid',
				omniSearch: [
					{
						route: '/personas/:uid',
						label: 'Personas',
						key: 'personas',
						breadcrumbs: ['Customers', 'Personas'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['personas', 'persona'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					// Hide the personas page for all non-whitelisted users (for uid: 2654)
					if (ctx.uid === '2654' && !['mbutera@pax.com', 'sally@pax.com'].includes(ctx.email)) return userUtils.isAdmin();
					// Hide persona tab for every user except admin in UID 2883
					if (ctx.uid === '2883') return userUtils.isAdmin();
					return adminOr(ctx.hasRole.manager);
				},
			},
			{
				label: 'Audiences',
				route: '/audiences/manage/:uid/AIQ',
				omniSearch: [
					{
						route: '/audiences/manage/:uid/AIQ',
						label: 'Audiences',
						key: 'audiences',
						breadcrumbs: ['Customers', 'Audiences'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['audiences', 'audience', 'segments', 'segment'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					return adminOr(ctx.hasRole.analyst && (hasLoyalty('starter') || hasMarketing('starter')));
				},
			},
			{
				label: 'Signup Forms',
				route: '/signup-forms/:uid',
				omniSearch: [
					{
						route: '/signup-forms/:uid',
						label: 'Signup Forms',
						key: 'signup-forms',
						breadcrumbs: ['Customers', 'Signup Forms'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['signup', 'forms', 'signup form', 'sign up'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					return adminOr(ctx.hasRole.owner && (hasLoyalty('starter') || hasMarketing('starter')));
				},
			},
			{
				label: 'Surveys',
				route: '/surveys/:uid',
				omniSearch: [
					{
						route: '/surveys/:uid',
						label: 'Surveys',
						key: 'surveys',
						breadcrumbs: ['Customers', 'Surveys'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['surveys', 'survey', 'survey-form', 'survey form'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					return adminOr(ctx.hasRole.analyst && (hasMarketing('pro') || (hasLoyalty('pro') && utils.isBrand())));
				},
			},
			{
				label: 'Reviews',
				route: '/reviews/:uid',
				omniSearch: [
					{
						route: '/reviews/:uid',
						label: 'Reviews',
						key: 'reviews',
						breadcrumbs: ['Customers', 'Reviews'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['reviews', 'review', 'review-form', 'ratings', 'rating'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					return adminOr(ctx.hasRole.analyst && (hasMarketing('pro') || (hasLoyalty('pro') && utils.isBrand())));
				},
			},
			{
				label: 'Receipt Lookup',
				route: '/receiptLookup/:uid',
				omniSearch: [
					{
						route: '/receiptLookup/:uid',
						label: 'Receipt Lookup',
						key: 'receipt-lookup',
						breadcrumbs: ['Customers', 'Receipt Lookup'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['receipt', 'lookup', 'transaction'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					return adminOr(ctx.hasRole.analyst && utils.isRetail());
				},
			},
		],
	},
	{
		label: 'Marketing',
		icon: 'MARKETING',
		isShown: inverted(isViewingAgency),
		children: [
			{
				label: 'Campaigns',
				route: '/campaign-manage/:uid',
				omniSearch: [
					{
						route: '/campaign-manage/:uid',
						label: 'Campaigns',
						key: 'campaign-manage',
						breadcrumbs: ['Marketing', 'Campaigns'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['campaigns', 'campaign', 'text', 'email', 'channel', 'marketing'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
				},
			},
			{
				label: 'Reporting',
				route: '/campaign-reports/:uid',
				omniSearch: [
					{
						route: '/campaign-reports/:uid',
						label: 'Campaign Reports',
						key: 'campaign-reporting',
						breadcrumbs: ['Marketing', 'Campaign Reports'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['campaigns', 'campaign', 'reports', 'report', 'stats', 'statistics', 'analytics'],
					},
					{
						route: '/campaign-reports/:uid',
						label: 'Reporting',
						key: 'reporting',
						breadcrumbs: ['Marketing', 'Reporting'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['campaigns', 'campaign', 'reports', 'report', 'stats', 'statistics', 'analytics'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
				},
			},
			{
				label: 'Legacy Co-Marketing',
				route: utils.isBrand() ? '/templates/:uid/manage' : '/templates/:uid/co-marketing',
				omniSearch: [
					{
						route: utils.isBrand() ? '/templates/:uid/manage' : '/templates/:uid/co-marketing',
						label: 'Co-Marketing',
						key: 'co-marketing',
						breadcrumbs: ['Marketing', 'Co-Marketing'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['co-marketing', 'templates', 'manage', 'co marketing'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					if (!utils.isCannabis() && !utils.isBevAlc()) return false;
					return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
				},
			},
			{
				label: 'Discounts',
				route: '/discounts/manage/:uid',
				omniSearch: [
					{
						route: '/discounts/manage/:uid',
						label: 'Discounts',
						key: 'discounts',
						breadcrumbs: ['Marketing', 'Discounts'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['discounts', 'discount', 'promotions', 'promotion'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					return adminOr(ctx.hasRole.marketing && hasLoyalty('starter'));
				},
			},
			{
				label: 'Templates',
				route: '/templates/:uid/templates',
				omniSearch: [
					{
						route: '/templates/:uid/templates',
						label: 'Templates',
						key: 'templates',
						breadcrumbs: ['Marketing', 'Templates'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['templates', 'marketing', 'co-marketing'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					if (!utils.isCannabis() && !utils.isBevAlc()) return false;
					return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
				},
			},
			{
				label: 'Media Library',
				route: '/mediaLibrary/:uid',
				omniSearch: [
					{
						route: '/mediaLibrary/:uid',
						label: 'Media Library',
						key: 'media-library',
						breadcrumbs: ['Marketing', 'Media Library'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['media', 'library', 'upload'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
				},
			},
			{
				label: 'Chat Inbox',
				route: '/chat/:uid',
				omniSearch: [
					{
						route: '/chat/:uid',
						label: 'Chat Inbox',
						key: 'chat-inbox',
						breadcrumbs: ['Marketing', 'Chat Inbox'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['chat', 'inbox', 'chat inbox'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					return adminOr(ctx.hasRole.marketing && isPoliticalAgency());
				},
			},
			{
				label: 'Planner',
				route: '/planner/:uid',
				omniSearch: [
					{
						route: '/planner/:uid',
						label: 'Planner',
						key: 'planner',
						breadcrumbs: ['Marketing', 'Planner'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['planner'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
				},
			},
		],
	},
	{
		label: 'Analytics',
		icon: 'ANALYTICS',
		isShown: inverted(isViewingAgency),
		children: [
			{
				label: 'My Analytics',
				route: '/analysis/:uid',
				omniSearch: [
					{
						route: '/analysis/:uid',
						label: 'My Analytics',
						key: 'my-analytics',
						breadcrumbs: ['Analytics', 'My Analytics'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['analytics', 'my analytics'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					if (!utils.isRetail()) return false;
					if (ctx.isRetailAnalyticsV2Enabled) return false;
					return adminOr(ctx.hasRole.analyst && hasRetailAnalytics('starter'));
				},
			},
			{
				label: 'Brand Analytics',
				route: '/brandAnalysis/:uid',
				omniSearch: [
					{
						route: '/brandAnalysis/:uid',
						label: 'Brand Analytics',
						key: 'brand-analytics',
						breadcrumbs: ['Analytics', 'Brand Analytics'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['analytics', 'brand analytics'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					if (ctx.isBrandAnalyticsV2Enabled) return false;
					return adminOr(ctx.hasRole.analyst && hasBrandAnalytics('brand-starter'));
				},
			},
			{
				label: 'Market Spotlight',
				route: '/marketSpotlight/:uid',
				omniSearch: [
					{
						route: '/marketSpotlight/:uid',
						label: 'Market Spotlight',
						key: 'market-spotlight',
						breadcrumbs: ['Analytics', 'Market Spotlight'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['analytics', 'market', 'spotlight', 'market spotlight'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					const specialAccess = ['1683', '1444', '1055', '1042', '1794', '1091'];
					return adminOr(ctx.hasRole.analyst && (specialAccess.includes(utils.uid) || hasBrandAnalytics('brand-starter')));
				},
			},
			{
				label: 'Recurring Reports',
				route: `/scheduledReports/:uid/${utils.isRetail() ? 'retail' : 'brand'}`,
				omniSearch: [
					{
						route: `/scheduledReports/:uid/${utils.isRetail() ? 'retail' : 'brand'}`,
						label: 'Recurring Reports',
						key: 'recurring-reports',
						breadcrumbs: ['Analytics', 'Recurring Reports'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['recurring', 'reports'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isPoliticalAgency()) return false;
					return adminOr(ctx.hasRole.analyst && (hasRetailAnalytics('pro') || hasBrandAnalytics('brand-starter')));
				},
			},
		],
	},
	{
		label: 'Retail Analytics',
		route: '/retailAnalytics/:uid',
		omniSearch: [
			{
				route: '/retailAnalytics/:uid',
				label: 'My Analytics',
				key: 'retail-analytics',
				breadcrumbs: ['Analytics', 'My Analytics'],
				type: OmniSearchTargetType.INTERNAL_LINK,
				keywords: ['analytics', 'retail analytics'],
			},
		],
		icon: 'ANALYTICS',
		isShown: (props: AiqNavLink, ctx: NavbarContext) => {
			if (isViewingAgency(props, ctx)) return false;
			if (isPoliticalAgency()) return false;
			if (!utils.isRetail()) return false;
			if (!ctx.isRetailAnalyticsV2Enabled) return false;
			return adminOr(ctx.hasRole.analyst && hasRetailAnalytics('starter'));
		},
	},
	{
		label: 'Brand Analytics',
		route: '/brandAnalytics/:uid',
		omniSearch: [
			{
				route: '/brandAnalytics/:uid',
				label: 'Brand Analytics',
				key: 'brand-analytics',
				breadcrumbs: ['Analytics', 'Brand Analytics'],
				type: OmniSearchTargetType.INTERNAL_LINK,
				keywords: ['analytics', 'brand analytics'],
			},
		],
		icon: 'ANALYTICS',
		isShown: (props: AiqNavLink, ctx: NavbarContext) => {
			if (isViewingAgency(props, ctx)) return false;
			if (isPoliticalAgency()) return false;
			if (!ctx.isBrandAnalyticsV2Enabled) return false;
			return adminOr(ctx.hasRole.analyst && hasBrandAnalytics('brand-starter'));
		},
	},
	{
		label: 'Ecommerce',
		icon: 'STORE',
		route: '/ecommerce/:uid',
		omniSearch: [
			{
				route: '/ecommerce/:uid',
				label: 'Ecommerce',
				key: 'ecommerce',
				breadcrumbs: ['Ecommerce'],
				type: OmniSearchTargetType.INTERNAL_LINK,
				keywords: ['ecommerce', 'store', 'ecom'],
			},
		],
		isShown: (props: AiqNavLink, ctx: NavbarContext) => {
			if (isViewingAgency(props, ctx)) return false;
			if (isPoliticalAgency()) return false;
			return adminOr(ctx.hasRole.owner || (ctx.hasRole.marketing && ctx.hasEcommerce));
		},
	},
	{
		label: 'Settings',
		icon: 'SETTINGS_GEAR',
		children: [
			{
				label: 'Company Account',
				children: [
					{
						label: 'Account Settings',
						route: '/settings/:uid/accountsettings',
						omniSearch: [
							{
								route: '/settings/:uid/accountsettings',
								label: 'Account Settings',
								key: 'account-settings',
								breadcrumbs: ['Settings', 'Company Account', 'Account Info'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['account settings', 'account', 'account info', 'company account'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => adminOr(ctx.hasRole.owner),
					},
					{
						label: 'Branding',
						route: '/settings/:uid/branding',
						omniSearch: [
							{
								route: '/settings/:uid/branding',
								label: 'Branding',
								key: 'branding',
								breadcrumbs: ['Settings', 'Company Account', 'Branding'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['branding', 'brand'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => adminOr(ctx.hasRole.owner),
					},
					{
						label: 'Team Members',
						route: '/settings/:uid/team',
						omniSearch: [
							{
								route: '/settings/:uid/team',
								label: 'Team Members',
								key: 'team-members',
								breadcrumbs: ['Settings', 'Company Account', 'Team Members'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['team', 'members'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => adminOr(ctx.hasRole.owner),
					},
					{
						label: 'Members Settings',
						route: '/settings/:uid/members',
						isShown: (props: AiqNavLink, ctx: NavbarContext) => adminOr(ctx.hasRole.owner),
					},
					{
						label: 'Email / Domain',
						route: '/settings/:uid/email',
						omniSearch: [
							{
								route: '/settings/:uid/email',
								label: 'Email / Domain',
								key: 'email-domain',
								breadcrumbs: ['Settings', 'Company Account', 'Email / Domain'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['email', 'domain'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
						},
					},
					{
						label: 'Retail Stores',
						route: '/settings/:uid/retailstores',
						omniSearch: [
							{
								route: '/settings/:uid/retailstores',
								label: 'Retail Stores',
								key: 'retail-stores',
								breadcrumbs: ['Settings', 'Company Account', 'Retail Stores'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['retail', 'stores'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							return adminOr(ctx.hasRole.marketing) && (utils.isRetail() || utils.isSandbox());
						},
					},
					{
						label: 'Brand Stores',
						route: '/settings/:uid/brandstores',
						omniSearch: [
							{
								route: '/settings/:uid/brandstores',
								label: 'Brand Stores',
								key: 'brand-stores',
								breadcrumbs: ['Settings', 'Company Account', 'Brand Stores'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['brand', 'stores'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							if (isPoliticalAgency()) return false;
							return adminOr(ctx.hasRole.marketing) && utils.isBrand();
						},
					},
					{
						label: 'My Products',
						route: '/settings/:uid/products',
						omniSearch: [
							{
								route: '/settings/:uid/products',
								label: 'My Products',
								key: 'my-products',
								breadcrumbs: ['Settings', 'Company Account', 'My Products'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['products', 'my products'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							if (isPoliticalAgency()) return false;
							return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
						},
					},
					{
						label: 'Reviews',
						route: '/settings/:uid/reviews',
						omniSearch: [
							{
								route: '/settings/:uid/reviews',
								label: 'Reviews',
								key: 'reviews',
								breadcrumbs: ['Settings', 'Company Account', 'Reviews'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['reviews'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							if (isPoliticalAgency()) return false;
							return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
						},
					},
					{
						label: 'Activity Logs',
						route: '/settings/:uid/activity',
						omniSearch: [
							{
								route: '/settings/:uid/activity',
								label: 'Activity Logs',
								key: 'activity-logs',
								breadcrumbs: ['Settings', 'Company Account', 'Activity Logs'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['activity', 'logs'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							return adminOr(ctx.hasRole.owner);
						},
					},
					{
						label: 'Support History',
						route: '/settings/:uid/tickets',
						omniSearch: [
							{
								route: '/settings/:uid/tickets',
								label: 'Support History',
								key: 'support-history',
								breadcrumbs: ['Settings', 'Company Account', 'Support History'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['support', 'history'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							return adminOr(ctx.hasRole.owner);
						},
					},
				],
			},
			{
				label: 'Alerts & Reports',
				children: [
					{
						label: ALERTS_MANAGER,
						// No route provided – leaving omniSearch out.
						route: '/settings/:uid/alerts',
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							if (isPoliticalAgency()) return false;
							return adminOr(ctx.hasRole.owner && hasDataOps('starter'));
						},
					},
					{
						label: 'Points Reporting',
						route: '/settings/:uid/loyalty/reporting',
						omniSearch: [
							{
								route: '/settings/:uid/loyalty/reporting',
								label: 'Points Reporting',
								key: 'points-reporting',
								breadcrumbs: ['Settings', 'Alerts & Reports', 'Points Reporting'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['points', 'reporting', 'loyalty'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							return adminOr((ctx.hasRole.owner || userUtils.getRealRole() === Role.FINANCIAL) && hasLoyalty('starter'));
						},
					},
					{
						label: 'Recurring Reports',
						route: `/settings/:uid/scheduled-reports/${utils.isRetail() ? 'retail' : 'brand'}`,
						omniSearch: [
							{
								route: `/settings/:uid/scheduled-reports/${utils.isRetail() ? 'retail' : 'brand'}`,
								label: 'Recurring Reports',
								key: 'recurring-reports',
								breadcrumbs: ['Settings', 'Alerts & Reports', 'Recurring Reports'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['recurring', 'reports'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isPoliticalAgency()) return false;
							return adminOr(ctx.hasRole.analyst && (hasRetailAnalytics('pro') || hasBrandAnalytics('brand-starter')));
						},
					},
				],
			},
			{
				label: 'Integrations',
				route: '/marketplace/:uid',
				omniSearch: [
					{
						route: '/marketplace/:uid',
						label: 'Integrations',
						key: 'integrations',
						breadcrumbs: ['Settings', 'Integrations'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['integrations', 'marketplace'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isViewingAgency(props, ctx)) return false;
					return adminOr(ctx.hasRole.owner && hasDataOps('starter'));
				},
			},
			{
				label: 'Texting',
				route: '/settings/:uid/texting',
				omniSearch: [
					{
						route: '/settings/:uid/texting',
						label: 'Texting',
						key: 'texting',
						breadcrumbs: ['Settings', 'Texting'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['texting'],
					},
					{
						route: '/settings/:uid/texting',
						label: 'Filtration Protection Level',
						key: 'filtration-protection-level',
						breadcrumbs: ['Settings', 'Texting', 'Filtration Protection Level'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['filtration', 'protection', 'level', 'texting'],
						scrollId: 'filtration-protection-level',
					},
					{
						route: '/settings/:uid/texting',
						label: 'Opt-in Framework',
						key: 'opt-in-framework',
						breadcrumbs: ['Settings', 'Texting', 'Opt-in Framework'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['opt-in', 'framework', 'texting'],
						scrollId: 'opt-in-framework',
					},
					{
						route: '/settings/:uid/texting',
						label: 'Default Text Message Templates',
						key: 'default-text-message-templates',
						breadcrumbs: ['Settings', 'Texting', 'Default Text Message Templates'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['default', 'text', 'message', 'templates', 'texting', 'logo'],
						scrollId: 'default-text-message-templates',
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isViewingAgency(props, ctx)) return false;
					return adminOr(ctx.hasRole.marketing && hasMarketing('starter'));
				},
			},
			{
				label: 'Loyalty',
				icon: 'LOYALTY',
				route: '/settings/:uid/loyalty/main',
				omniSearch: [
					{
						route: '/settings/:uid/loyalty/main',
						label: 'Loyalty',
						key: 'loyalty',
						breadcrumbs: ['Settings', 'Loyalty'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty'],
					},
					{
						route: '/settings/:uid/loyalty/main',
						label: 'General Accural Settings',
						key: 'general-accrual-settings',
						breadcrumbs: ['Settings', 'Loyalty', 'General Accural Settings'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty', 'general', 'accrual', 'settings'],
						scrollId: 'general-accrual-settings',
					},
					{
						route: '/settings/:uid/loyalty/main',
						label: 'Accrual By Customer Type',
						key: 'accrual-by-customer-type',
						breadcrumbs: ['Settings', 'Loyalty', 'Accrual By Customer Type'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty', 'accrual', 'customer', 'type'],
						scrollId: 'accrual-by-customer-type',
					},
					{
						route: '/settings/:uid/loyalty/main',
						label: 'Restrict Accrual By Persona',
						key: 'restrict-accrual-by-persona',
						breadcrumbs: ['Settings', 'Loyalty', 'Restrict Accrual By Persona'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty', 'accrual', 'persona', 'type'],
						scrollId: 'restrict-accrual-by-persona',
					},
					{
						route: '/settings/:uid/loyalty/main',
						label: 'Accrue For Referrals',
						key: 'accrue-for-referrals',
						breadcrumbs: ['Settings', 'Loyalty', 'Accrue For Referrals'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty', 'accrual', 'referrals'],
						scrollId: 'accrue-for-referrals',
					},
					{
						route: '/settings/:uid/loyalty/main',
						label: 'Expiration',
						key: 'expiration',
						breadcrumbs: ['Settings', 'Loyalty', 'Expiration'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty', 'expiration'],
						scrollId: 'expiration',
					},
					{
						route: '/settings/:uid/loyalty/main',
						label: 'Redemption Overrides',
						key: 'redemption-overrides',
						breadcrumbs: ['Settings', 'Loyalty', 'Redemption Overrides'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty', 'redemption', 'overrides'],
						scrollId: 'redemption-overrides',
					},
					{
						route: '/settings/:uid/loyalty/main',
						label: 'Staff App Overrides',
						key: 'staff-app-overrides',
						breadcrumbs: ['Settings', 'Loyalty', 'Staff App Overrides'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty', 'staff', 'app', 'overrides'],
						scrollId: 'staff-app-overrides',
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isViewingAgency(props, ctx)) return false;
					return adminOr((ctx.hasRole.owner || userUtils.getRealRole() === Role.FINANCIAL) && hasLoyalty('starter'));
				},
			},
			{
				label: 'Mobile App',
				render(props, ctx) {
					if (ctx.isLoyaltyAppV2Enabled || hasLoyaltyAppV2Enabled()) {
						return 'Legacy Mobile App';
					}
					return 'Mobile App';
				},
				route: '/settings/:uid/appsettings',
				omniSearch: [
					{
						route: '/settings/:uid/appsettings',
						label: 'Mobile App',
						key: 'loyalty-app',
						breadcrumbs: ['Settings', 'Mobile App'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['loyalty', 'app'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (adminUtils.canSeeAll()) return true;
					return adminOr(ctx.hasRole.marketing && (hasApp('starter') || hasLoyalty('starter')));
				},
			},
			{
				label: hasLoyaltyAppV2Enabled() ? 'Mobile App' : 'Mobile App V2 (Preview)',
				render: (props: AiqNavLink, ctx: NavbarContext) => {
					if (hasLoyaltyAppV2Enabled()) {
						return 'Mobile App';
					}
					return 'Mobile App V2 (Preview)';
				},
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isViewingAgency(props, ctx)) return false;
					if (!ctx.isLoyaltyAppV2Enabled && !hasLoyaltyAppV2Enabled()) return false;
					return adminOr(ctx.hasRole.marketing && (hasApp('starter') || hasLoyalty('starter')));
				},
				children: [
					{
						label: 'App Info',
						route: '/settings/:uid/loyalty-app/info',
						omniSearch: [
							{
								route: '/settings/:uid/loyalty-app/info',
								label: 'App Info',
								key: 'app-info',
								breadcrumbs: ['Settings', 'Mobile App V2 (Preview)', 'App Info'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['app', 'info'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => true,
					},
					{
						label: 'App Styling',
						render: (props: AiqNavLink, ctx: NavbarContext) => {
							const hasAppStyling = userUtils.hasPackageTier('nativeApps', 'starter');
							if (!hasAppStyling) {
								return (
									<Flex align="center">
										App Styling <LightningIcon />
									</Flex>
								);
							}
							return undefined;
						},
						route: '/settings/:uid/loyalty-app/styling',
						omniSearch: [
							{
								route: '/settings/:uid/loyalty-app/styling',
								label: 'App Styling',
								key: 'app-styling',
								breadcrumbs: ['Settings', 'Mobile App V2 (Preview)', 'App Styling'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['app', 'styling'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => true,
					},
					{
						label: 'App Features',
						route: '/settings/:uid/loyalty-app/features',
						omniSearch: [
							{
								route: '/settings/:uid/loyalty-app/features',
								label: 'App Features',
								key: 'app-features',
								breadcrumbs: ['Settings', 'Mobile App V2 (Preview)', 'App Features'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['app', 'features'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => true,
					},
					{
						label: 'App Verbiage',
						route: '/settings/:uid/loyalty-app/verbiage',
						omniSearch: [
							{
								route: '/settings/:uid/loyalty-app/verbiage',
								label: 'App Verbiage',
								key: 'app-verbiage',
								breadcrumbs: ['Settings', 'Mobile App V2 (Preview)', 'App Verbiage'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['app', 'verbiage'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => true,
					},
					{
						label: 'App Admin',
						route: '/settings/:uid/loyalty-app/admin',
						omniSearch: [
							{
								route: '/settings/:uid/loyalty-app/admin',
								label: 'App Admin',
								key: 'app-admin',
								breadcrumbs: ['Settings', 'Mobile App V2 (Preview)', 'App Admin'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['app', 'admin'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => true,
					},
				],
			},
			// {
			// 	label: 'Import Personas',
			// 	route: '/fileUploads/:uid',
			// 	isShown: (props: AiqNavLink, ctx: NavbarContext) => {
			// 		if (isViewingAgency(props, ctx)) return false;
			// 		return adminOr(ctx.hasRole.owner && hasDataOps('starter'));
			// 	},
			// },
			{
				label: 'Data & Privacy',
				route: '/settings/:uid/userdata',
				omniSearch: [
					{
						route: '/settings/:uid/userdata',
						label: 'Data & Privacy',
						key: 'data-privacy',
						breadcrumbs: ['Settings', 'Data & Privacy'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['data', 'privacy'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isViewingAgency(props, ctx)) return false;
					return adminOr(ctx.hasRole.owner);
				},
			},
			{
				label: 'Billing & History',
				children: [
					{
						label: 'Billing Plans',
						route: '/settings/:uid/billing/general',
						omniSearch: [
							{
								route: '/settings/:uid/billing/general',
								label: 'Billing Plans',
								key: 'billing-plans',
								breadcrumbs: ['Settings', 'Billing & History', 'Billing Plans'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['billing', 'plans'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							if (userUtils.hasLegacyBilling()) return false;
							return adminOr(ctx.hasRole.owner);
						},
					},
					{
						label: 'Billing History',
						route: '/settings/:uid/billing/history',
						omniSearch: [
							{
								route: '/settings/:uid/billing/history',
								label: 'Billing History',
								key: 'billing-history',
								breadcrumbs: ['Settings', 'Billing & History', 'Billing History'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['billing', 'history'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							return adminOr(ctx.hasRole.owner);
						},
					},
					{
						label: 'Transactional Msgs',
						route: '/settings/:uid/billing/transactional',
						omniSearch: [
							{
								route: '/settings/:uid/billing/transactional',
								label: 'Transactional Msgs',
								key: 'transactional-msgs',
								breadcrumbs: ['Settings', 'Billing & History', 'Transactional Msgs'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['transactional', 'messages', 'billing'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isViewingAgency(props, ctx)) return false;
							const showTransactionalWhitelist = ['1211', '2443', '1505', '2646'];
							const showTransactional = showTransactionalWhitelist.includes(utils.uid) || userUtils.isAdmin();
							return adminOr(ctx.hasRole.owner && showTransactional);
						},
					},
					{
						label: 'Billing Admin',
						route: '/settings/:uid/admin',
						omniSearch: [
							{
								route: '/settings/:uid/admin',
								label: 'Billing Admin',
								key: 'billing-admin',
								breadcrumbs: ['Settings', 'Billing & History', 'Billing Admin'],
								type: OmniSearchTargetType.INTERNAL_LINK,
								keywords: ['billing', 'admin'],
							},
						],
						isShown: (props: AiqNavLink, ctx: NavbarContext) => {
							if (isPoliticalAgency() && adminOr()) return true;
							if (isViewingAgency(props, ctx)) return false;
							return adminOr();
						},
					},
				],
			},
			{
				label: 'API & Tracking',
				route: '/settings/:uid/apitracking',
				omniSearch: [
					{
						route: '/settings/:uid/apitracking',
						label: 'API & Tracking',
						key: 'api-tracking',
						breadcrumbs: ['Settings', 'API & Tracking'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['api', 'tracking'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => {
					if (isViewingAgency(props, ctx)) return false;
					const canNotSeeApiAndTracking = utils.isFreemium() && utils.uid !== '2638';
					return adminOr(ctx.hasRole.owner && !canNotSeeApiAndTracking);
				},
			},
		],
	},
	{
		label: 'Admin',
		icon: 'ADMIN',
		children: [
			{
				label: 'Enterprises',
				route: '/admin',
				omniSearch: [
					{
						route: '/admin',
						label: 'Enterprises',
						key: 'enterprises',
						breadcrumbs: ['Admin', 'Enterprises'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['enterprises', 'admin'],
					},
				],
				isShown: () => adminOr(),
				exactRoute: true,
			},
			{
				label: 'Companies (WIP)',
				route: '/admin/companies',
				omniSearch: [
					{
						route: '/admin/companies',
						label: 'Companies (WIP)',
						key: 'companies-wip',
						breadcrumbs: ['Admin', 'Companies (WIP)'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['companies', 'wip'],
					},
				],
				isShown: () => userUtils.isExperimental() && adminOr(),
			},
			{
				label: 'Company Search',
				route: '/search',
				omniSearch: [
					{
						route: '/search',
						label: 'Company Search',
						key: 'company-search',
						breadcrumbs: ['Admin', 'Company Search'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['company', 'search'],
					},
				],
				isShown: () => adminOr(),
			},
			{
				label: 'Account Renewals',
				route: '/admin/accounts/renewals',
				omniSearch: [
					{
						route: '/admin/accounts/renewals',
						label: 'Account Renewals',
						key: 'account-renewals',
						breadcrumbs: ['Admin', 'Account Renewals'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['account', 'renewals'],
					},
				],
				isShown: () => adminOr(),
			},
			{
				label: 'Domain Filtration',
				route: '/domainHealth',
				omniSearch: [
					{
						route: '/domainHealth',
						label: 'Domain Filtration',
						key: 'domain-filtration',
						breadcrumbs: ['Admin', 'Domain Filtration'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['domain', 'filtration'],
					},
				],
				isShown: () => adminOr(),
			},
			{
				label: 'Dashboard Alerts',
				route: '/userAlerts/1',
				omniSearch: [
					{
						route: '/userAlerts/1',
						label: 'Dashboard Alerts',
						key: 'dashboard-alerts',
						breadcrumbs: ['Admin', 'Dashboard Alerts'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['dashboard', 'alerts'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => !!(adminUtils.canViewUserAlerts() ?? false),
			},
			{
				label: 'Login Finder',
				route: '/loginLookup',
				omniSearch: [
					{
						route: '/loginLookup',
						label: 'Login Finder',
						key: 'login-finder',
						breadcrumbs: ['Admin', 'Login Finder'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['login', 'finder'],
					},
				],
				isShown: () => adminOr(),
			},
			{
				label: 'Receipt Lookup',
				route: '/receiptLookup/1',
				omniSearch: [
					{
						route: '/receiptLookup/1',
						label: 'Receipt Lookup',
						key: 'receipt-lookup',
						breadcrumbs: ['Admin', 'Receipt Lookup'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['receipt', 'lookup'],
					},
				],
				isShown: () => adminOr(),
			},
			{
				label: 'Greenbits Token',
				route: '/greenbits',
				omniSearch: [
					{
						route: '/greenbits',
						label: 'Greenbits Token',
						key: 'greenbits-token',
						breadcrumbs: ['Admin', 'Greenbits Token'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['greenbits', 'token'],
					},
				],
				isShown: () => adminOr(),
			},
			{
				label: 'Media Library',
				route: '/admin/mediaLibrary/1',
				omniSearch: [
					{
						route: '/admin/mediaLibrary/1',
						label: 'Global Media Library',
						key: 'admin-media-library',
						breadcrumbs: ['Admin', 'Media Library'],
						type: OmniSearchTargetType.INTERNAL_LINK,
						keywords: ['media', 'library'],
					},
				],
				isShown: (props: AiqNavLink, ctx: NavbarContext) => !!((adminUtils.canViewUserAlerts() || adminUtils.canSeeAll()) ?? false),
			},
		],
	},
] as const;

/**
 * Recursively returns a flat array of all nav links (including children)
 * that are shown based on the provided context.
 */
export function getFlatShownNavLinks(ctx: NavbarContext, navLinks: AiqNavLink[] = aiqAppNavLinks, layer = 0): AiqNavLink[] {
	const flat: AiqNavLink[] = [];

	for (const link of navLinks) {
		const isVisible = link.isShown ? link.isShown(link, ctx) : true;
		// If user is viewing as admin they see all links no matter what
		if (isVisible || (userUtils.isAdmin() && !isViewingAgency(link, ctx))) {
			flat.push(link);
			if (link.children && link.children.length > 0) {
				flat.push(...getFlatShownNavLinks(ctx, link.children, layer + 1));
			}
		}
	}

	return flat;
}

/**
 * Given a flat list of AiqNavLinks, returns a flat array of all OmniSearchOption items.
 */
export function getOmniSearchOptions(flatNavLinks: AiqNavLink[]): OmniSearchOption[] {
	return flatNavLinks.reduce((acc: OmniSearchOption[], link) => {
		if (link.omniSearch && Array.isArray(link.omniSearch)) {
			acc.push(...link.omniSearch);
		}
		return acc;
	}, []);
}

// SECTION : Navbar Utils

// Matches route to pathname, accounts for :uid as a utils.uid value
export const isMatchingRoute = (route?: string, exactRoute = false) => {
	if (!route) return false;
	if (exactRoute) {
		return window.location.pathname === route.replace(':uid', utils.uid);
	}
	return window.location.pathname.includes(route.replace(':uid', utils.uid));
};

// Recursively check if the current route is active
export const isActiveLink = (aiqNavLink: AiqNavLink) => {
	const query = new URLSearchParams(window.location.search);
	const isCurrentActive = isMatchingRoute(aiqNavLink.route, aiqNavLink.exactRoute);
	const isAlertManagerOpen = query.get('userAlertManager') === 'true';
	if (isAlertManagerOpen && aiqNavLink.label === ALERTS_MANAGER) {
		return true;
	}
	if (!isCurrentActive && aiqNavLink.children) {
		return aiqNavLink.children.some(isActiveLink);
	}
	return isCurrentActive;
};

// Handle the click event on a nav link. Redirect if it has route, and use onClick if it exists
export const handleClickLink = async (e: React.MouseEvent<HTMLDivElement | HTMLAnchorElement>, aiqNavLink: AiqNavLink, ctx: NavbarContext) => {
	e.preventDefault();
	await aiqNavLink.onClick?.(e as React.MouseEvent<HTMLDivElement>);
	if (aiqNavLink.route) {
		const isMiddleClick = e.button === 1;
		let url = aiqNavLink.route;
		if (utils.isMessageDigital() && aiqNavLink.label === 'Campaigns') {
			url = '/campaigns/manage/:uid/ACTIVE';
		}
		if (isMiddleClick) {
			window.open(url.replace(':uid', utils.uid), isMiddleClick ? '_blank' : '_self');
			return;
		}
		utils.history.push(url.replace(':uid', utils.uid));
		capturePosthogEvent('$pageview');
		return;
	}
	if (!aiqNavLink.onClick) {
		const nextNav = getFirstClickableChild(aiqNavLink, ctx);
		handleClickLink(e, nextNav, ctx);
	}
	return;
};

export const getFirstClickableChild = (aiqNavLink: AiqNavLink, ctx: NavbarContext, deep = false): AiqNavLink => {
	if (aiqNavLink.children) {
		for (const child of aiqNavLink.children) {
			if (canClickChild(child, ctx, true, deep)) {
				return getFirstClickableChild(child, ctx, true);
			}
		}
	}
	return aiqNavLink;
};

export const shouldShowNavItem = (aiqNavLink: AiqNavLink, ctx: NavbarContext, requireRoute = false): boolean => {
	const canNotShow = aiqNavLink.isShown?.(aiqNavLink, ctx) === false;
	const canClick = canClickChild(aiqNavLink, ctx, requireRoute);
	const hasChildren = !!aiqNavLink.children?.length;
	if (canNotShow || (!canClick && !hasChildren)) {
		return false;
	}
	return canClick || aiqNavLink.children?.some((child) => shouldShowNavItem(child, ctx)) || false;
};

const canClickChild = (aiqNavLink: AiqNavLink, ctx: NavbarContext, requireRoute?: boolean, deep = false) => {
	if (deep && aiqNavLink.children) {
		for (const child of aiqNavLink.children) {
			if (canClickChild(child, ctx, requireRoute, true)) {
				return true;
			}
		}
	}
	const isClickable = aiqNavLink.route || aiqNavLink.onClick;
	if (requireRoute && !aiqNavLink.route) return false;
	const canNotShow = aiqNavLink.isShown?.(aiqNavLink, ctx) === false;
	const isDisabled = aiqNavLink.isDisabled?.(aiqNavLink);
	return isClickable && !isDisabled && !canNotShow;
};

// SECTION: Page Title Utils

/**
 * Finds a navigation item that matches the given path
 * @param path The path to match against
 * @param navLinks The navigation links to search
 * @param uid The user ID to substitute in routes
 * @returns The matching navigation item or undefined
 */
export const findMatchingNavItem = (
	path: string,
	navLinks: AiqNavLink[] = aiqAppNavLinks,
	uid: string = utils.uid,
): { navItem: AiqNavLink; params: Record<string, string> } | undefined => {
	// Look for exact matches first
	for (const link of navLinks) {
		if (link.route) {
			// Convert route patterns to regex for matching with named capture groups
			const routePattern = link.route.replace(':uid', uid);

			// Convert :paramName to named capture groups (?<paramName>[^/]+)
			const regexPattern = routePattern.replace(/\:([^\/]+)/g, '(?<$1>[^/]+)');
			const regex = new RegExp(`^${regexPattern}$`);

			const match = path.match(regex);

			if (match) {
				// Extract parameters from named capture groups
				const params: Record<string, string> = match.groups || {};
				return { navItem: link, params };
			}
		}

		// Check children recursively
		if (link.children?.length) {
			const match = findMatchingNavItem(path, link.children, uid);
			if (match) return match;
		}
	}

	return undefined;
};

/**
 * Get a page title based on specific path patterns and query parameters
 * Contains all the special case logic for determining page titles
 */
export const getPathSpecificTitle = (path: string): string | null => {
	// Handle some special cases that might not be in the nav structure
	// This maintains backward compatibility with the old UsePageViews.tsx implementation
	if (path.includes('/templates/') && path.includes('/builder')) {
		const params = new URLSearchParams(window.location.search);
		const recipe = params.get('recipe') || '';
		return `Template Builder ${recipe ? `(${recipe})` : ''}`;
	}

	if (path.includes('/templates/') && path.includes('/co-marketing')) {
		return 'Co-Marketing Templates';
	}

	if (path.includes('/audience/builder')) {
		const audienceIdMatch = path.match(/\/audience\/builder\/\w+\/(\w+)/);
		const audienceId = audienceIdMatch?.[1];
		return audienceId ? `Audience Builder (${audienceId})` : 'Audience Builder';
	}

	if (path.includes('/campaign/builder')) {
		const campaignIdMatch = path.match(/\/campaign\/builder\/\w+\/(\w+)/);
		const campaignId = campaignIdMatch?.[1];
		return campaignId ? `Campaign Builder (${campaignId})` : 'Campaign Builder';
	}

	if (path.includes('/campaign-manage')) {
		// Depending on the query params, we can determine the page title
		const params = new URLSearchParams(window.location.search);
		// campaigns, sponsoring, sponsored, installed
		const tab = params.get('tab');
		const campaignID = params.get('campaignID');

		if (tab === 'campaigns') {
			return 'Campaigns';
		}
		if (tab === 'sponsoring') {
			return 'Sponsoring';
		}

		if (tab === 'sponsored') {
			return 'Sponsored';
		}
		if (tab === 'installed') {
			return 'Installed';
		}

		return campaignID ? `Campaign Manager (${campaignID})` : 'Campaign Manager';
	}

	if (path.includes('/discount/builder')) {
		const templateIdMatch = path.match(/\/discount\/builder\/\w+\/(\w+)/);
		const templateId = templateIdMatch?.[1];
		return templateId ? `Discount Builder (${templateId})` : 'Discount Builder';
	}

	if (path.includes('/settings/') && path.includes('/signup/builder')) {
		const formIdMatch = path.match(/\/signup\/builder\/(\w+)/);
		const formId = formIdMatch?.[1];
		return formId ? `Signup Builder (${formId})` : 'Signup Builder';
	}

	if (path.includes('/settings/') && path.includes('/tab/')) {
		const tabMatch = path.match(/\/settings\/\w+\/(\w+)\/(\w+)/);
		const tab = tabMatch?.[1];
		const subTab = tabMatch?.[2];

		if (tab && subTab) {
			return `Settings (${utils.capitalize(tab)} - ${utils.capitalize(subTab)})`;
		}
	}

	if (path.includes('/settings/') && path.includes('/tab')) {
		const tabMatch = path.match(/\/settings\/\w+\/(\w+)/);
		const tab = tabMatch?.[1];

		if (tab) {
			return `Settings (${utils.capitalize(tab)})`;
		}
	}

	// Surveys special case
	if (path.includes('/surveys/')) {
		const params = new URLSearchParams(window.location.search);
		const surveyId = params.get('surveyID');
		return surveyId ? `Survey (${surveyId})` : 'Surveys';
	}

	// Return null if no specific title was found
	return null;
};

/**
 * Gets the page title for the current path using the navigation structure
 * @param path The current path
 * @param uid The user ID
 * @returns The page title
 */
export const getPageTitle = (path: string = window.location.pathname, uid: string = utils.uid): string => {
	// First check for path-specific titles
	const pathSpecificTitle = getPathSpecificTitle(path);
	if (pathSpecificTitle) {
		return pathSpecificTitle;
	}

	// Try to find a match in the navigation structure
	const matchResult = findMatchingNavItem(path, aiqAppNavLinks, uid);

	if (matchResult) {
		const { navItem, params } = matchResult;

		// If the navItem has a getTitle function, use it
		if (navItem.getTitle) {
			const queryParams = new URLSearchParams(window.location.search);
			const query: Record<string, string> = {};

			// Convert queryParams to a simple object (avoiding direct iterator)
			queryParams.forEach((value, key) => {
				query[key] = value;
			});

			return navItem.getTitle(params, query);
		}

		// Otherwise use the label
		if (navItem.label) {
			return typeof navItem.label === 'string' ? navItem.label : 'AIQ';
		}
	}

	// Fallback to a capitalized path
	return utils.capitalize(path.replace(/\//g, ' ').trim());
};

/**
 * Sets the document title based on the current path
 */
export const setPageTitle = (uid: string = utils.uid): void => {
	const path = window.location.pathname;
	const title = getPageTitle(path, uid);

	// Extract UID from path if present
	const uidMatch = path.match(/\/([^/]+)/);
	const pathUid = uidMatch?.[1];
	const userID = (uid || (pathUid && !Number.isNaN(Number(pathUid))) ? pathUid : utils.uid) || '';

	const invalidUIDs = ['1', '2'];
	const isValidUID = !invalidUIDs.includes(userID);

	// Set document title with the same format as before
	document.title = `${userUtils.isAdmin() && isValidUID ? `${uid} ` : ''}${title}${userUtils.isAdmin() ? '' : ' | AIQ'}`;
};
